import { useState, useEffect, useCallback, useId, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Plus, Trash2 } from "lucide-react";
import type { QlikDimension, QlikField } from "@repo/shared";
import {
  Button,
  Combobox,
  Field,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
  type SelectOption,
} from "@h2j-data-team/ui";
import { QlikExpressionEditor } from "@/components/qlik/qlik-expression-editor";
import { pickerMessages } from "@/lib/ui-labels";
import type { PendingMasterItemChange, PendingMasterItemCreate } from "@/stores/pending-changes";

// ============================================================
// FieldCombobox: a data-model field, or a free expression (=Year(Date))
// ============================================================

function FieldCombobox({
  value,
  onChange,
  fields,
  disabled,
  label,
}: {
  value: string;
  onChange: (value: string) => void;
  fields: QlikField[] | undefined;
  disabled?: boolean;
  label: string;
}) {
  const { t } = useTranslation("single-app");

  const options = useMemo<SelectOption[]>(() => {
    const names = new Set((fields ?? []).map((f) => f.name));
    return [...names].map((name) => ({
      value: name,
      label: name,
      group: t("masterItems.fieldCombobox.groupHeading"),
    }));
  }, [fields, t]);

  // A typed expression is not an option: it is created from the search text and shown by name.
  // The picker sizes itself, so the row gives it the free width through a wrapper.
  return (
    <div className="min-w-0 flex-1 [&_.h2j-picker-trigger]:w-full [&_.h2j-picker]:w-full">
      <Combobox
        aria-label={label}
        className="font-mono"
        options={options}
        value={value || null}
        displayLabel={value || undefined}
        onValueChange={(next) => onChange(next ?? "")}
        onCreate={onChange}
        createLabel={(query) => t("masterItems.fieldCombobox.useExpression", { query })}
        disabled={disabled}
        messages={pickerMessages({
          placeholder: t("masterItems.fieldCombobox.buttonPlaceholder"),
          searchPlaceholder: t("masterItems.fieldCombobox.inputPlaceholder"),
          emptyLabel: t("masterItems.fieldCombobox.empty"),
        })}
      />
    </div>
  );
}

// ============================================================
// DimensionDetailForm
// ============================================================

export interface DimensionChanges {
  title?: string;
  description?: string;
  fieldDefs?: string[];
  fieldLabels?: string[];
  grouping?: "N" | "H" | "C";
  labelExpression?: string;
  tags?: string[];
}

export interface DimensionDetailFormProps {
  /** Effective base object. For a brand-new item, pass a blank template. */
  item: QlikDimension;
  isCreate: boolean;
  existingChange?: PendingMasterItemChange;
  existingCreate?: PendingMasterItemCreate;
  canEdit: boolean;
  /** Called with the diff (only changed fields) on any field change/blur. */
  onChange: (changes: DimensionChanges) => void;
  fields?: QlikField[];
}

/** Internal field row state */
interface FieldRow {
  def: string;
  label: string;
}

/** Derives initial field rows from defs + labels arrays. */
function buildFieldRows(defs: string[], labels: string[]): FieldRow[] {
  if (defs.length === 0) return [{ def: "", label: "" }];
  return defs.map((d, i) => ({ def: d, label: labels[i] ?? "" }));
}

/**
 * Computes effective prefill values from the pending-create, pending-change,
 * or base item — matching the exact priority used in EditDimensionDialog.
 */
function computeInitialState(
  item: QlikDimension,
  isCreate: boolean,
  existingCreate: PendingMasterItemCreate | undefined,
  existingChange: PendingMasterItemChange | undefined,
): {
  title: string;
  description: string;
  fields: FieldRow[];
  grouping: "N" | "H" | "C";
  labelExpression: string;
  tagsText: string;
} {
  if (isCreate && existingCreate) {
    const defs = existingCreate.fieldDefs ?? [];
    const labels = existingCreate.fieldLabels ?? [];
    return {
      title: existingCreate.title,
      description: existingCreate.description ?? "",
      fields: buildFieldRows(defs, labels),
      grouping: (item.grouping as "N" | "H" | "C") ?? "N",
      labelExpression: existingCreate.labelExpression ?? "",
      tagsText: (existingCreate.tags ?? []).join(", "),
    };
  }

  if (existingChange) {
    const defs = existingChange.changes.fieldDefs ?? item.fieldDefs;
    const labels = existingChange.changes.fieldLabels ?? item.fieldLabels ?? [];
    return {
      title: existingChange.changes.title ?? item.title,
      description: existingChange.changes.description ?? item.description ?? "",
      fields: buildFieldRows(defs, labels),
      grouping: (existingChange.changes.grouping ?? item.grouping ?? "N") as "N" | "H" | "C",
      labelExpression: existingChange.changes.labelExpression ?? item.labelExpression ?? "",
      tagsText: (existingChange.changes.tags ?? item.tags).join(", "),
    };
  }

  return {
    title: item.title,
    description: item.description ?? "",
    fields: buildFieldRows(item.fieldDefs, item.fieldLabels ?? []),
    grouping: (item.grouping as "N" | "H" | "C") ?? "N",
    labelExpression: item.labelExpression ?? "",
    tagsText: item.tags.join(", "),
  };
}

/**
 * Dimension detail form (in-panel, NOT a dialog).
 *
 * Auto-commits diffs to the parent on every field change/blur.
 * There is no per-item Save button — the global Save all is used instead.
 */
export function DimensionDetailForm({
  item,
  isCreate,
  existingChange,
  existingCreate,
  canEdit,
  onChange,
  fields: availableFields,
}: DimensionDetailFormProps) {
  const { t } = useTranslation("single-app");
  const groupId = useId();

  // ---- local form state ----
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [fields, setFields] = useState<FieldRow[]>([{ def: "", label: "" }]);
  const [grouping, setGrouping] = useState<"N" | "H" | "C">("N");
  const [labelExpression, setLabelExpression] = useState("");
  const [tagsText, setTagsText] = useState("");

  // ---- sync when item/existingChange/existingCreate change ----
  useEffect(() => {
    const init = computeInitialState(item, isCreate, existingCreate, existingChange);
    setTitle(init.title);
    setDescription(init.description);
    setFields(init.fields);
    setGrouping(init.grouping);
    setLabelExpression(init.labelExpression);
    setTagsText(init.tagsText);
  }, [item, isCreate, existingCreate, existingChange]);

  // ---- auto-trim to 1 field when switching to Single ----
  useEffect(() => {
    if (grouping === "N" && fields.length > 1) {
      setFields((prev) => [prev[0]]);
    }
  }, [grouping]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- diff builder: matches EditDimensionDialog.handleSubmit logic ----
  const buildDiff = useCallback(
    (
      nextTitle: string,
      nextDescription: string,
      nextFields: FieldRow[],
      nextGrouping: "N" | "H" | "C",
      nextLabelExpression: string,
      nextTagsText: string,
    ): DimensionChanges => {
      const validFields = nextFields.filter((f) => f.def.trim());
      const fieldDefs = validFields.map((f) => f.def.trim());
      const fieldLabels = nextGrouping !== "N" ? validFields.map((f) => f.label.trim()) : undefined;
      const tags = nextTagsText
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);

      const diff: DimensionChanges = {};

      if (isCreate) {
        if (nextTitle !== (existingCreate?.title ?? "")) diff.title = nextTitle;
        if (nextDescription !== (existingCreate?.description ?? ""))
          diff.description = nextDescription;
        const origDefs = existingCreate?.fieldDefs ?? [];
        const origLabels = existingCreate?.fieldLabels ?? [];
        if (
          JSON.stringify(fieldDefs) !== JSON.stringify(origDefs) ||
          (fieldLabels && JSON.stringify(fieldLabels) !== JSON.stringify(origLabels))
        ) {
          diff.fieldDefs = fieldDefs;
          diff.fieldLabels = fieldLabels;
        }
        const origLabelExpr = existingCreate?.labelExpression ?? "";
        if (nextGrouping === "N" && nextLabelExpression.trim() !== origLabelExpr) {
          diff.labelExpression = nextLabelExpression.trim();
        }
        const origTags = (existingCreate?.tags ?? []).join(", ");
        if (nextTagsText.trim() !== origTags.trim()) diff.tags = tags;
      } else {
        if (nextTitle !== item.title) diff.title = nextTitle;
        if (nextDescription !== (item.description ?? "")) diff.description = nextDescription;
        if (
          JSON.stringify(fieldDefs) !== JSON.stringify(item.fieldDefs) ||
          (fieldLabels && JSON.stringify(fieldLabels) !== JSON.stringify(item.fieldLabels ?? []))
        ) {
          diff.fieldDefs = fieldDefs;
          diff.fieldLabels = fieldLabels;
        }
        if (nextGrouping !== ((item.grouping ?? "N") as "N" | "H" | "C"))
          diff.grouping = nextGrouping;
        const origLabelExpr = item.labelExpression ?? "";
        if (nextGrouping === "N" && nextLabelExpression.trim() !== origLabelExpr) {
          diff.labelExpression = nextLabelExpression.trim();
        }
        if (nextTagsText.trim() !== item.tags.join(", ").trim()) diff.tags = tags;
      }

      return diff;
    },
    [isCreate, existingCreate, item],
  );

  // ---- dispatch helper: builds diff from current + override, fires onChange ----
  const dispatch = useCallback(
    (overrides: {
      title?: string;
      description?: string;
      fields?: FieldRow[];
      grouping?: "N" | "H" | "C";
      labelExpression?: string;
      tagsText?: string;
    }) => {
      const nextTitle = overrides.title ?? title;
      const nextDescription = overrides.description ?? description;
      const nextFields = overrides.fields ?? fields;
      const nextGrouping = overrides.grouping ?? grouping;
      const nextLabelExpression = overrides.labelExpression ?? labelExpression;
      const nextTagsText = overrides.tagsText ?? tagsText;

      const diff = buildDiff(
        nextTitle,
        nextDescription,
        nextFields,
        nextGrouping,
        nextLabelExpression,
        nextTagsText,
      );
      if (Object.keys(diff).length > 0) {
        onChange(diff);
      }
    },
    [title, description, fields, grouping, labelExpression, tagsText, buildDiff, onChange],
  );

  // ---- field-update helpers ----
  const handleTitleBlur = () => dispatch({ title });
  const handleDescriptionBlur = () => dispatch({ description });
  const handleTagsBlur = () => dispatch({ tagsText });

  const handleGroupingChange = (v: "N" | "H" | "C") => {
    let nextFields = fields;
    if (v === "N" && fields.length > 1) {
      nextFields = [fields[0]];
      setFields(nextFields);
    }
    setGrouping(v);
    dispatch({ grouping: v, fields: nextFields });
  };

  const handleFieldDefChange = (i: number, v: string) => {
    const next = [...fields];
    next[i] = { ...next[i], def: v };
    setFields(next);
    dispatch({ fields: next });
  };

  const handleFieldLabelChange = (i: number, v: string) => {
    const next = [...fields];
    next[i] = { ...next[i], label: v };
    setFields(next);
    // blur-commit for labels
  };

  const handleFieldLabelBlur = () => {
    dispatch({ fields });
  };

  const handleAddField = () => {
    const next = [...fields, { def: "", label: "" }];
    setFields(next);
  };

  const handleRemoveField = (i: number) => {
    const next = fields.filter((_, j) => j !== i);
    setFields(next);
    dispatch({ fields: next });
  };

  const handleLabelExpressionChange = (v: string) => {
    setLabelExpression(v);
    dispatch({ labelExpression: v });
  };

  return (
    <div className="grid gap-6">
      {/* Title */}
      <Field label={t("masterItems.editDimension.titleLabel")} id="dim-detail-title">
        <Input
          value={title}
          disabled={!canEdit}
          onChange={(e) => setTitle(e.target.value)}
          onBlur={handleTitleBlur}
          placeholder={t("masterItems.editDimension.titlePlaceholder")}
        />
      </Field>

      {/* Description */}
      <Field label={t("masterItems.editDimension.descriptionLabel")} id="dim-detail-description">
        <Textarea
          value={description}
          disabled={!canEdit}
          onChange={(e) => setDescription(e.target.value)}
          onBlur={handleDescriptionBlur}
          placeholder={t("masterItems.editDimension.descriptionPlaceholder")}
          className="min-h-[60px]"
        />
      </Field>

      {/* Grouping (type) */}
      <Field label={t("masterItems.editDimension.typeLabel")} id="dim-detail-grouping">
        {(control) => (
          <Select
            value={grouping}
            onValueChange={(v) => handleGroupingChange(v as "N" | "H" | "C")}
            disabled={!canEdit}
          >
            <SelectTrigger {...control}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="N">{t("masterItems.dimensions.typeSingle")}</SelectItem>
              <SelectItem value="H">{t("masterItems.dimensions.typeDrillDown")}</SelectItem>
              <SelectItem value="C">{t("masterItems.dimensions.typeCyclic")}</SelectItem>
            </SelectContent>
          </Select>
        )}
      </Field>

      {/* Fields */}
      <div role="group" aria-labelledby={`${groupId}-fields`} className="grid gap-2">
        <Label id={`${groupId}-fields`}>{t("masterItems.editDimension.fieldsLabel")}</Label>
        <div className="space-y-2">
          {fields.map((field, i) => (
            <div key={i} className="flex items-center gap-2">
              <FieldCombobox
                label={t("masterItems.editDimension.fieldN", { n: i + 1 })}
                value={field.def}
                onChange={(v) => handleFieldDefChange(i, v)}
                fields={availableFields}
                disabled={!canEdit}
              />
              {grouping !== "N" && (
                <Input
                  value={field.label}
                  disabled={!canEdit}
                  onChange={(e) => handleFieldLabelChange(i, e.target.value)}
                  onBlur={handleFieldLabelBlur}
                  placeholder={t("masterItems.editDimension.fieldLabelPlaceholder")}
                  aria-label={`${t("masterItems.editDimension.fieldLabelPlaceholder")} ${i + 1}`}
                  className="flex-1 text-sm"
                />
              )}
              {fields.length > 1 && canEdit && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  className="shrink-0"
                  aria-label={t("masterItems.editDimension.removeField", { n: i + 1 })}
                  onClick={() => handleRemoveField(i)}
                >
                  <Trash2 />
                </Button>
              )}
            </div>
          ))}
        </div>
        {grouping !== "N" && canEdit && (
          <div>
            <Button type="button" variant="outline" size="sm" onClick={handleAddField}>
              <Plus /> {t("masterItems.editDimension.addField")}
            </Button>
          </div>
        )}
      </div>

      {/* Label expression — only for Single (grouping === "N") */}
      {grouping === "N" && (
        <Field
          label={t("masterItems.editDimension.labelExpressionLabel")}
          id="dim-detail-label-expr"
        >
          {(control) => (
            <QlikExpressionEditor
              id={control.id}
              ariaLabel={t("masterItems.editDimension.labelExpressionLabel")}
              value={labelExpression}
              onChange={handleLabelExpressionChange}
              placeholder={t("masterItems.editDimension.labelExpressionPlaceholder")}
              minHeight="120px"
              readOnly={!canEdit}
              showFx
            />
          )}
        </Field>
      )}

      {/* Tags */}
      <Field label={t("masterItems.editDimension.tagsLabel")} id="dim-detail-tags">
        <Input
          value={tagsText}
          disabled={!canEdit}
          onChange={(e) => setTagsText(e.target.value)}
          onBlur={handleTagsBlur}
          placeholder={t("masterItems.editDimension.tagsPlaceholder")}
        />
      </Field>
    </div>
  );
}

export default DimensionDetailForm;
